import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { defaultRouteFor, routePermission, useAuth } from './auth'
import { AppShell } from './components/AppShell'
import { ProductDataBoundary } from './components/ProductDataBoundary'
import { DataStatusPage } from './pages/DataStatusPage'
import { AnalysisPage } from './pages/AnalysisPage'
import { OverviewPage } from './pages/OverviewPage'
import { ProductDetailPage } from './pages/ProductDetailPage'
import { ProductListPage } from './pages/ProductListPage'
import { ProductSegmentsPage } from './pages/ProductSegmentsPage'
import { ProductMasterPage } from './pages/ProductMasterPage'
import { NewProductsPage } from './pages/NewProductsPage'
import { InventoryPage } from './pages/InventoryPage'
import { CampaignAnalysisPage } from './pages/CampaignAnalysisPage'
import { AdvertisingPage } from './pages/AdvertisingPage'
import { JdAdvertisingOrderDetailPage } from './pages/JdAdvertisingOrderDetailPage'
import { JdFeeRatioPage } from './pages/JdFeeRatioPage'
import { ProfitCenterPage } from './pages/ProfitCenterPage'
import { RealtimePage } from './pages/RealtimePage'
import { PinduoduoPage } from './pages/PinduoduoPage'
import { CompanyOverviewPage } from './pages/CompanyOverviewPage'
import { PinduoduoProductsPage } from './pages/PinduoduoProductsPage'
import { PinduoduoProductDetailPage } from './pages/PinduoduoProductDetailPage'
import { StoreCostProfilePage } from './pages/StoreCostProfilePage'
import { CompanyProductMappingsPage } from './pages/CompanyProductMappingsPage'
import { CompanyProductLibraryPage } from './pages/CompanyProductLibraryPage'
import { PinduoduoSkuCatalogPage, PinduoduoSkuMasterPage, PinduoduoSpuCatalogPage, PinduoduoSpuDetailPage } from './pages/PinduoduoCatalogPages'
import { PinduoduoSkuDetailPage } from './pages/PinduoduoSkuDetailPage'
import { PinduoduoAfterSalesPage } from './pages/PinduoduoAfterSalesPage'
import { PinduoduoDataUploadPage } from './pages/PinduoduoDataUploadPage'
import { PinduoduoProfitCalculatorPage } from './pages/PinduoduoProfitCalculatorPage'
import { DouyinSpuListPage } from './pages/DouyinSpuListPage'
import { DouyinSpuDetailPage } from './pages/DouyinSpuDetailPage'
import { DouyinSkuListPage } from './pages/DouyinSkuListPage'
import { DouyinLiveSessionsPage } from './pages/DouyinLiveSessionsPage'
import { DouyinLiveSessionDetailPage } from './pages/DouyinLiveSessionDetailPage'
import { JdProfitCalculatorPage } from './pages/JdProfitCalculatorPage'
import { JdDataUpdatePage } from './pages/JdDataUpdatePage'
import { JdOrdersPage } from './pages/JdOrdersPage'
import { JdWarehouseSlowMovingPage } from './pages/JdWarehouseSlowMovingPage'
import { LoginPage } from './pages/LoginPage'
import { ChangePasswordPage } from './pages/ChangePasswordPage'
import { ForbiddenPage } from './pages/ForbiddenPage'
import { AccountManagementPage } from './pages/AccountManagementPage'
import { SharedCalculatorRecordPage } from './pages/SharedCalculatorRecordPage'
import { normalizeDouyinStoreId } from './douyinStore'

function DouyinCostProfileRoute() {
  const { storeId } = useParams<{ storeId: string }>()
  return <StoreCostProfilePage storeId={normalizeDouyinStoreId(storeId)} />
}

function ProtectedRoutes() {
  const auth = useAuth()
  const location = useLocation()
  if (auth.loading) return <div className="auth-loading">正在确认登录与权限…</div>
  if (!auth.user) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  if (auth.user.mustChangePassword) return <Navigate to="/change-password" replace />
  if (location.pathname === '/admin/accounts' && !auth.user.isAdmin) return <ForbiddenPage />
  const permission = routePermission(location.pathname)
  if (permission && !auth.canAccess(permission[0], permission[1])) return <ForbiddenPage />
  return <Outlet />
}

function SiteRoutes() {
  const { user } = useAuth()
  const returnRoute = user ? defaultRouteFor(user) : '/login'
  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />
      <Route path="change-password" element={<ChangePasswordPage />} />
      <Route element={<ProtectedRoutes />}>
       <Route element={<AppShell />}>
        <Route path="forbidden" element={<ForbiddenPage />} />
        <Route path="admin/accounts" element={<AccountManagementPage />} />
        <Route path="company" element={<CompanyOverviewPage />} />
        <Route path="company/products" element={<CompanyProductLibraryPage />} />
        <Route path="company/mappings" element={<CompanyProductMappingsPage />} />
        <Route path="pinduoduo" element={<PinduoduoPage />} />
        <Route path="pinduoduo/products" element={<PinduoduoProductsPage />} />
        <Route path="pinduoduo/spu" element={<PinduoduoSpuCatalogPage />} />
        <Route path="pinduoduo/spu/:productId" element={<PinduoduoSpuDetailPage />} />
        <Route path="pinduoduo/sku" element={<PinduoduoSkuCatalogPage />} />
        <Route path="pinduoduo/sku/:variantId" element={<PinduoduoSkuDetailPage />} />
        <Route path="pinduoduo/product-master" element={<PinduoduoSkuMasterPage />} />
        <Route path="pinduoduo/product/:productId" element={<PinduoduoProductDetailPage />} />
        <Route path="pinduoduo/costs" element={<StoreCostProfilePage storeId="pdd-beijuxiong-youhe" />} />
        <Route path="pinduoduo/after-sales" element={<PinduoduoAfterSalesPage />} />
        <Route path="pinduoduo/data-upload" element={<PinduoduoDataUploadPage />} />
        <Route path="pinduoduo/profit-calculator" element={<PinduoduoProfitCalculatorPage />} />
        <Route path="douyin/:storeId/spu" element={<DouyinSpuListPage />} />
        <Route path="douyin/:storeId/spu/:productId" element={<DouyinSpuDetailPage />} />
        <Route path="douyin/:storeId/sku" element={<DouyinSkuListPage />} />
        <Route path="douyin/:storeId/live" element={<DouyinLiveSessionsPage />} />
        <Route path="douyin/:storeId/live/:sessionId" element={<DouyinLiveSessionDetailPage />} />
        <Route path="douyin/:storeId/costs" element={<DouyinCostProfileRoute />} />
        <Route path="douyin/spu" element={<DouyinSpuListPage />} />
        <Route path="douyin/spu/:productId" element={<DouyinSpuDetailPage />} />
        <Route path="douyin/sku" element={<DouyinSkuListPage />} />
        <Route path="douyin/live" element={<DouyinLiveSessionsPage />} />
        <Route path="douyin/live/:sessionId" element={<DouyinLiveSessionDetailPage />} />
        <Route path="douyin/costs" element={<StoreCostProfilePage storeId="dy-beddypop-shuiju" />} />
        <Route path="jd/costs" element={<StoreCostProfilePage storeId="jd-588485" />} />
        <Route path="jd/profit-calculator" element={<JdProfitCalculatorPage />} />
        <Route path="jd/data-update" element={<JdDataUpdatePage />} />
        <Route path="jd/orders" element={<JdOrdersPage />} />
        <Route path="jd/warehouse-slow-moving" element={<JdWarehouseSlowMovingPage />} />
        <Route path="shared-calculations/:platform/:recordId" element={<SharedCalculatorRecordPage />} />
        <Route path="realtime" element={<RealtimePage />} />
        <Route path="advertising/fee-ratio" element={<JdFeeRatioPage />} />
        <Route path="advertising/spend-comparison" element={<Navigate to="/advertising/fee-ratio" replace />} />
        <Route element={<ProductDataBoundary />}>
          <Route index element={<OverviewPage />} />
          <Route path="profit" element={<ProfitCenterPage />} />
          <Route path="spu" element={<ProductListPage level="spu" />} />
          <Route path="sku" element={<ProductListPage level="sku" />} />
          <Route path="analysis" element={<AnalysisPage mode="overview" />} />
          <Route path="analysis/business" element={<AnalysisPage mode="business" />} />
          <Route path="analysis/traffic" element={<AnalysisPage mode="traffic" />} />
          <Route path="analysis/advertising" element={<AnalysisPage mode="advertising" />} />
          <Route path="analysis/refunds" element={<AnalysisPage mode="refunds" />} />
          <Route path="advertising" element={<AdvertisingPage />} />
          <Route path="advertising/orders" element={<JdAdvertisingOrderDetailPage />} />
          <Route path="product-master" element={<ProductMasterPage />} />
          <Route path="new-products" element={<NewProductsPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="campaign-analysis" element={<CampaignAnalysisPage />} />
          <Route path="segments" element={<ProductSegmentsPage />} />
          <Route path="product/:level/:id" element={<ProductDetailPage />} />
          <Route path="data" element={<DataStatusPage />} />
        </Route>
        <Route path="*" element={<div className="not-found"><h1>页面不存在</h1><a href={returnRoute}>返回可用页面</a></div>} />
       </Route>
      </Route>
    </Routes>
  )
}

export default function App() { return <SiteRoutes /> }
